import { useCallback, useMemo } from 'react';

import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useNonWithdrawableVerificationCheck } from '@/feature/account-details/hooks/use-non-withdrawable-verification-check';
import { useFantasyMoneyFlags } from '@/hooks/payments/use-fantasy-money-flags';
import { RootStackParamList } from '@/navigation/types';
import { WithdrawStackNavigationProp } from '@/navigation/withdraw/types';

import { filterWithdrawableMethods, getWithdrawHomeRoute } from '../utils/utils';
import { selectAllWithdrawMethods, useWithdrawalMethods } from './use-withdrawal-methods';

/**
 * Get the initial route for the withdraw flow based on the available withdrawal methods and user verification status.
 */
const useGetWithdrawHomeRoute = () => {
    const { data: withdrawMethods = [] } = useWithdrawalMethods({ select: selectAllWithdrawMethods });
    const { isFantasyMoneyRedemptionEnabled } = useFantasyMoneyFlags();
    const { isVerifiedWithRestrictedBalance, needsVerificationByLightSdd } = useNonWithdrawableVerificationCheck();

    const filteredWithdrawalMethods = useMemo(
        () =>
            filterWithdrawableMethods(withdrawMethods, {
                isVerifiedWithRestrictedBalance: !!isVerifiedWithRestrictedBalance,
                needsVerificationByLightSdd: !!needsVerificationByLightSdd,
            }),
        [withdrawMethods, isVerifiedWithRestrictedBalance, needsVerificationByLightSdd]
    );

    return useMemo(
        () => getWithdrawHomeRoute(filteredWithdrawalMethods, isFantasyMoneyRedemptionEnabled),
        [filteredWithdrawalMethods, isFantasyMoneyRedemptionEnabled]
    );
};

/** Starts the withdraw flow from outside it (from account) */
export const useOpenWithdrawStack = () => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const route = useGetWithdrawHomeRoute();

    return useCallback(() => navigation.navigate('WithdrawStack', route), [navigation, route]);
};

/**
 * Navigates to the Withdraw home inside the stack navigator
 * TODO: Future improvement after refactor: pop to the existing screen instead of pushing a new one.
 * needs further changes if we use popTo/navigate with `pop: true`
 * kept as navigate to keep current behavior unchanged/consistent
 */
export const useGoToWithdrawHome = () => {
    const navigation = useNavigation<WithdrawStackNavigationProp>();
    const route = useGetWithdrawHomeRoute();

    return useCallback(
        () =>
            route.screen === 'WithdrawForm'
                ? navigation.push(route.screen, route.params)
                : navigation.push(route.screen),
        [navigation, route]
    );
};
